import React from "react";
import {Button, Grid} from "@material-ui/core";
import {connect, MapDispatchToProps, MapStateToProps} from "react-redux";
import {showRejectAttorneyModal, showResubmitCaseModal} from "../../vendors/actions/modals";
import {CASE_STATUS, isMobileBrowser} from "../../vendors/utils/common";

/**
 * The two exits the consumer has from a matched or dead case: reject the matched lawyer,
 * and resubmit.
 *
 * Gates, all keyed on the SERVER's status numbers (see CASE_STATUS):
 *
 * - Reject Attorney shows ONLY at matched (2) with a lawyer on the case. Past that the case
 *   has work on it and the server refuses (400_50); before it there is nobody to reject.
 *   Cancel/Delete stays where it is - pending (0) only - so the two never overlap.
 *
 * - Resubmit is gated on the SERVER's `canResubmit`, which GET /case/:case_uid now returns.
 *   It is produced by calling the real gate (UnmatchValidators.validateResubmit fed by
 *   UnmatchModel.wasUnmatched - see server vendors/unmatchReadState), so the button and the
 *   endpoint cannot disagree. This matters because nothing else in the payload separates a
 *   case that reached canceled (5) through an unmatch from one a pre-match Delete canceled,
 *   and only the former may be resubmitted; the old `status === 5` gate offered the button
 *   on both and let the second fail inside the modal.
 *
 *   `status === CASE_STATUS.canceled` remains as the fallback for a payload without the
 *   field (an older API, or a cached response) - i.e. exactly the previous behaviour.
 */

interface OwnProps {
    caseData: any
}

interface ConnectStateProps {
}

interface ConnectDispatchProps {
    showRejectAttorneyModal: (bool: boolean) => void;
    showResubmitCaseModal: (bool: boolean) => void;
}

type Props = OwnProps & ConnectStateProps & ConnectDispatchProps

const CaseActions = (props: Props) => {
    const {caseData} = props;
    const status = caseData?.status;

    const canRejectAttorney = status === CASE_STATUS.matched && !!caseData?.matchedLawyer?.id;
    const canResubmit = typeof caseData?.canResubmit === 'boolean'
        ? caseData.canResubmit
        : status === CASE_STATUS.canceled;

    // Nothing to offer while an unmatch request is out (20) or under arbitration (21) - the
    // consumer only waits, and notificationBox/notifications.ts explains the wait.
    if (!canRejectAttorney && !canResubmit) return null;

    const buttonSize = isMobileBrowser() ? 'medium' : 'large';

    return (
        <Grid container className='px-4 pb-4 case-actions'>
            <Grid item xs={12} lg={11}>
                <div className='d-flex justify-content-center justify-content-md-end buttons-wrapper'>
                    {canRejectAttorney &&
                    <Button
                        color="default"
                        onClick={() => props.showRejectAttorneyModal(true)}
                        size={buttonSize}
                        variant="outlined"
                        className="mx-1 text-black-50">
                        <span className="btn-wrapper--label">Reject Attorney</span>
                    </Button>}
                    {canResubmit &&
                    <Button
                        color="primary"
                        onClick={() => props.showResubmitCaseModal(true)}
                        size={buttonSize}
                        variant="contained"
                        className="mx-1">
                        <span className="btn-wrapper--label font-size-md">Resubmit</span>
                    </Button>}
                </div>
            </Grid>
        </Grid>
    )
};

type ConnectStateMapper = MapStateToProps<ConnectStateProps, OwnProps>

const mapStateToProps: ConnectStateMapper = () => {
    return {}
};

type ConnectDispatchMapper = MapDispatchToProps<ConnectDispatchProps, OwnProps>

const mapDispatchToProps: ConnectDispatchMapper = (dispatch: any) => {
    return {
        showRejectAttorneyModal: (data: boolean) => dispatch(showRejectAttorneyModal(data)),
        showResubmitCaseModal: (data: boolean) => dispatch(showResubmitCaseModal(data))
    }
};

export {CaseActions};

export default connect(mapStateToProps, mapDispatchToProps)(CaseActions);
